import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  Grid,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccountBalance as SalaireIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { attestationsSalaireAPI, employeesAPI, auditLogsAPI } from '../../../services/api';
import { AttestationSalaire, AttestationSalaireCreate } from '../../../types/attestationSalaire';
import { Employee, EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from '../../../components/MinimizableDialog';
// generateAttestationSalairePDF is dynamically imported in handleGeneratePDF
// so the jspdf chunk only loads when the user requests a PDF.

const HR_COLOR = '#00897B';

const ATT_SALAIRE_FILTER_SCHEMA = {
  ref:       { type: 'text', param: 'ref' },
  date:      { type: 'dateRange', paramFrom: 'date_from', paramTo: 'date_to' },
  matricule: { type: 'text', param: 'matricule' },
  employe:   { type: 'text', param: 'employe' },
  poste:     { type: 'text', param: 'poste' },
} as const;

// Helper function to convert number to French words
const numberToFrenchWords = (num: number): string => {
  const units = ['', 'UN', 'DEUX', 'TROIS', 'QUATRE', 'CINQ', 'SIX', 'SEPT', 'HUIT', 'NEUF', 'DIX', 'ONZE', 'DOUZE', 'TREIZE', 'QUATORZE', 'QUINZE', 'SEIZE', 'DIX-SEPT', 'DIX-HUIT', 'DIX-NEUF'];
  const tens = ['', '', 'VINGT', 'TRENTE', 'QUARANTE', 'CINQUANTE', 'SOIXANTE', 'SOIXANTE', 'QUATRE-VINGT', 'QUATRE-VINGT'];

  if (num === 0) return 'ZERO';
  if (num < 0) return 'MOINS ' + numberToFrenchWords(-num);

  const convertHundreds = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return units[n];
    if (n < 70) {
      const u = n % 10;
      const t = Math.floor(n / 10);
      if (u === 1 && t !== 8) return tens[t] + ' ET UN';
      if (u === 0) return tens[t];
      return tens[t] + '-' + units[u];
    }
    if (n < 80) {
      const u = n - 60;
      if (u === 1) return 'SOIXANTE ET ONZE';
      return 'SOIXANTE-' + units[u];
    }
    if (n < 100) {
      const u = n - 80;
      if (u === 0) return 'QUATRE-VINGTS';
      return 'QUATRE-VINGT-' + units[u];
    }
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h === 1) return 'CENT' + (r > 0 ? ' ' + convertHundreds(r) : (r === 0 ? '' : ''));
    return units[h] + ' CENT' + (r === 0 ? 'S' : ' ' + convertHundreds(r));
  };

  const convertThousands = (n: number): string => {
    if (n === 0) return '';
    if (n < 1000) return convertHundreds(n);
    const t = Math.floor(n / 1000);
    const r = n % 1000;
    const thousandPart = t === 1 ? 'MILLE' : convertHundreds(t) + ' MILLE';
    return thousandPart + (r > 0 ? ' ' + convertHundreds(r) : '');
  };

  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);

  let result = convertThousands(intPart) + ' DIRHAMS';
  if (decPart > 0) {
    result += ' ET ' + convertHundreds(decPart) + ' CENTIMES';
  }
  return result;
};

const AttestationSalaireManagement: React.FC = () => {
  const [attestations, setAttestations] = useState<AttestationSalaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [anneeFilter, setAnneeFilter] = useState(new Date().getFullYear());

  // Form dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(formDialogOpen);
  const [formData, setFormData] = useState<{
    employee_id: number | null;
    date_attestation: string;
    salaire_net: number;
    salaire_lettres: string;
    motif: string;
  }>({
    employee_id: null,
    date_attestation: new Date().toISOString().split('T')[0],
    salaire_net: 0,
    salaire_lettres: '',
    motif: '',
  });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attestationToDelete, setAttestationToDelete] = useState<AttestationSalaire | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  // Selected employee details
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState<Employee | null>(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(ATT_SALAIRE_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchAttestations = useCallback(async () => {
    setLoading(true);
    try {
      const extra = toQueryParams();
      const response = await attestationsSalaireAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        annee: anneeFilter,
        extra: Object.keys(extra).length > 0 ? extra : undefined,
      });
      setAttestations(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, anneeFilter, showError, filters]);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchAttestations();
  }, [fetchAttestations]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleAnneeChange = (event: SelectChangeEvent<number>) => {
    setAnneeFilter(event.target.value as number);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Cache an in-progress new attestation so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
  }>('attestation-salaire');

  const handleAddAttestation = () => {
    restore();
    const draft = loadDraft();
    if (draft) {
      setFormData(draft.formData);
      setSelectedEmployeeDetails(null);
      // Refresh the employee panel without re-auto-filling the (possibly edited) salary.
      if (draft.formData.employee_id) {
        employeesAPI
          .getById(draft.formData.employee_id)
          .then((emp) => setSelectedEmployeeDetails(emp))
          .catch(() => setSelectedEmployeeDetails(null));
      }
    } else {
      setFormData({
        employee_id: null,
        date_attestation: new Date().toISOString().split('T')[0],
        salaire_net: 0,
        salaire_lettres: '',
        motif: '',
      });
      setSelectedEmployeeDetails(null);
    }
    setFormDialogOpen(true);
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    saveDraft({ formData });
    setFormDialogOpen(false);
  };

  // Annuler = deliberate cancel: discard any draft.
  const handleCancelForm = () => {
    clearDraft();
    setMinimized(false);
    setFormDialogOpen(false);
  };

  const handleEmployeeSelect = async (employeeId: number | null) => {
    setFormData(prev => ({ ...prev, employee_id: employeeId }));

    if (employeeId) {
      setLoadingEmployee(true);
      try {
        const employee = await employeesAPI.getById(employeeId);
        setSelectedEmployeeDetails(employee);
        // Auto-fill salary from employee record if available
        if (employee.salaire_net) {
          const salaire = Number(employee.salaire_net);
          setFormData(prev => ({
            ...prev,
            salaire_net: salaire,
            salaire_lettres: numberToFrenchWords(salaire),
          }));
        }
      } catch (error) {
        console.error('Error fetching employee details:', error);
        setSelectedEmployeeDetails(null);
      } finally {
        setLoadingEmployee(false);
      }
    } else {
      setSelectedEmployeeDetails(null);
    }
  };

  const handleSalaireChange = (value: string) => {
    const salaire = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      salaire_net: salaire,
      salaire_lettres: salaire > 0 ? numberToFrenchWords(salaire) : '',
    }));
  };

  const handleFormSubmit = async () => {
    if (!formData.employee_id) {
      showError('Veuillez sélectionner un employé');
      return;
    }
    if (formData.salaire_net <= 0) {
      showError('Veuillez entrer un salaire valide');
      return;
    }

    try {
      const createData: AttestationSalaireCreate = {
        employee_id: formData.employee_id,
        date_attestation: formData.date_attestation,
        salaire_net: formData.salaire_net,
        salaire_lettres: formData.salaire_lettres || undefined,
        motif: formData.motif || undefined,
      };
      await attestationsSalaireAPI.create(createData);
      showSuccess('Attestation de salaire créée avec succès');
      clearDraft();
      setFormDialogOpen(false);
      fetchAttestations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDeleteClick = (attestation: AttestationSalaire) => {
    setAttestationToDelete(attestation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attestationToDelete) return;
    try {
      await attestationsSalaireAPI.delete(attestationToDelete.id);
      showSuccess('Attestation supprimée avec succès');
      fetchAttestations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setAttestationToDelete(null);
    }
  };

  const handleGeneratePDF = (attestation: AttestationSalaire) => {
    openPreview({
      getBlob: async () => {
        const { generateAttestationSalairePDF } = await import('../../../utils/attestationSalairePdfGenerator');
        return generateAttestationSalairePDF(attestation, true) as Blob;
      },
      fileName: `attestation_salaire_${attestation.ref_attestation || attestation.id}.pdf`,
    });
    auditLogsAPI.logPdfDownload('AttestationSalaire', attestation.id, `Aperçu PDF - ${attestation.ref_attestation}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Generate year options (from 2020 to current year + 1)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  // Find selected employee for autocomplete
  const selectedEmployee = employees.find(e => e.id === formData.employee_id) || null;

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SalaireIcon />
                <Typography variant="h6">{totalCount}</Typography>
              </Box>
              <Typography variant="body2">Total Attestations de Salaire</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employé..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Année</InputLabel>
            <Select value={anneeFilter} onChange={handleAnneeChange} label="Année">
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={fetchAttestations} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAttestation}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Nouvelle Attestation
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Réf.
                    <ColumnFilter type="text" value={filters.ref} onChange={(v) => update('ref', v)} label="référence" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Attestation
                    <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Matricule
                    <ColumnFilter type="text" value={filters.matricule} onChange={(v) => update('matricule', v)} label="matricule" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employé
                    <ColumnFilter type="text" value={filters.employe} onChange={(v) => update('employe', v)} label="employé" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Poste
                    <ColumnFilter type="text" value={filters.poste} onChange={(v) => update('poste', v)} label="poste" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="right">Salaire Net</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Chargement...</TableCell>
                </TableRow>
              ) : attestations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Aucune attestation trouvée</TableCell>
                </TableRow>
              ) : (
                attestations.map((attestation) => (
                  <TableRow key={attestation.id} hover>
                    <TableCell>
                      <Chip
                        label={attestation.ref_attestation}
                        size="small"
                        sx={{ bgcolor: '#9C27B0', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(attestation.date_attestation)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {attestation.employee?.matricule || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {attestation.employee?.nom_complet || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {attestation.employee?.fonction || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600" sx={{ color: '#2E7D32' }}>
                        {formatCurrency(attestation.salaire_net)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Générer PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleGeneratePDF(attestation)}
                            sx={{ color: '#1976D2' }}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(attestation)}
                            sx={{ color: '#EF5350' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nouvelle Attestation de Salaire
          <DialogControls color="white" onMinimize={minimize} onClose={handleCancelForm} />
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.matricule} - ${option.nom_complet}`}
              value={selectedEmployee}
              onChange={(_, newValue) => {
                handleEmployeeSelect(newValue?.id || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employé"
                  required
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* Employee Info Card */}
            {loadingEmployee && (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary">
                  Chargement des informations...
                </Typography>
              </Paper>
            )}

            {selectedEmployeeDetails && !loadingEmployee && (
              <Paper sx={{ p: 2, bgcolor: '#E0F2F1', border: `1px solid ${HR_COLOR}` }}>
                <Typography variant="subtitle2" sx={{ color: HR_COLOR, mb: 1, fontWeight: 'bold' }}>
                  Informations Employé
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Matricule</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.matricule}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Nom Complet</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.nom_complet}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">CIN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.cin || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">N° CNSS</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.numero_cnss || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Fonction</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.fonction || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date Embauche</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {selectedEmployeeDetails.date_embauche ? formatDate(selectedEmployeeDetails.date_embauche) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Type Contrat</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.type_contrat}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Salaire Net (fiche)</Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ color: '#2E7D32' }}>
                      {selectedEmployeeDetails.salaire_net ? formatCurrency(Number(selectedEmployeeDetails.salaire_net)) : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <TextField
              label="Date Attestation"
              type="date"
              value={formData.date_attestation}
              onChange={(e) => setFormData(prev => ({ ...prev, date_attestation: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Salaire Net Mensuel (DH)"
              type="number"
              value={formData.salaire_net || ''}
              onChange={(e) => handleSalaireChange(e.target.value)}
              fullWidth
              required
              InputProps={{
                endAdornment: <InputAdornment position="end">DH</InputAdornment>,
              }}
            />

            <TextField
              label="Salaire en Lettres"
              value={formData.salaire_lettres}
              onChange={(e) => setFormData(prev => ({ ...prev, salaire_lettres: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              helperText="Généré automatiquement, vous pouvez le modifier si nécessaire"
            />

            <TextField
              label="Motif (optionnel)"
              value={formData.motif}
              onChange={(e) => setFormData(prev => ({ ...prev, motif: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="Ex: Pour servir et valoir ce que de droit"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelForm} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={formDialogOpen && minimized}
        color="#009688"
        title="Nouvelle Attestation de Salaire"
        onRestore={restore}
        onClose={handleCancelForm}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'attestation ${attestationToDelete?.ref_attestation} de ${attestationToDelete?.employee?.nom_complet || 'cet employé'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default AttestationSalaireManagement;
